import { useParams, Link } from 'react-router-dom'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { DISCLAIMER } from '../data/peptides.js'
import { buildChart, CHART_SLUGS } from '../lib/dosingChart.js'
import { calculate } from '../lib/peptide.js'
import { faqLd } from '../lib/faq.js'
import { withBrand } from '../lib/seoTitle.js'
import CalculatorForm from '../components/CalculatorForm.jsx'
import Seo from '../components/Seo.jsx'
import LastUpdated from '../components/LastUpdated.jsx'

const unitLabel = (u) => (u === 'mg' ? 'mg' : 'mcg')
const fmtUnits = (n) => (n == null ? '—' : (Number.isInteger(n) ? `${n}` : n.toFixed(2).replace(/\.?0+$/, '')))

// Peptide-specific Q&A (also emitted as FAQPage structured data).
function buildFaq(p, chart) {
  const cp = chart.preset
  const t = calculate({ vialMg: cp.vialMg, waterMl: cp.waterMl, dose: cp.dose, doseUnit: cp.doseUnit, syringe: 's100' })
  if (!t.ok) return []
  const du = unitLabel(cp.doseUnit)
  return [
    {
      q: `How many units of ${p.name} should I draw?`,
      a: `Reconstituting a ${cp.vialMg} mg vial of ${p.name} with ${cp.waterMl} mL of bacteriostatic water gives ${t.concentrationMgMl} mg/mL. A ${cp.dose} ${du} dose is about ${fmtUnits(t.units)} units on a U-100 insulin syringe, roughly ${t.dosesPerVial} doses per vial. This is a commonly cited example for context, not medical advice.`,
    },
    {
      q: `How much bacteriostatic water do I add to ${p.name}?`,
      a: `There is no single correct amount. More water makes each dose a larger, easier-to-measure volume; less water makes it more concentrated. The chart above shows the units for 1–5 mL — ${cp.waterMl} mL is a common choice for a ${cp.vialMg} mg vial.`,
    },
    {
      q: `How many doses are in a ${cp.vialMg} mg ${p.name} vial?`,
      a: `At a ${cp.dose} ${du} dose, a ${cp.vialMg} mg vial holds about ${t.dosesPerVial} doses. Larger doses mean fewer doses per vial, and the amount of water you add does not change this.`,
    },
  ]
}

export default function DosingChart() {
  const { slug } = useParams()
  const { getBySlug, loading } = usePeptides()
  const p = getBySlug(slug)
  const chart = p ? buildChart(p) : null

  if (loading && !p) {
    return <main className="page"><div className="container center" style={{ padding: '60px 0' }}><div className="spinner" /></div></main>
  }
  if (!p || !chart) {
    return (
      <main className="page">
        <div className="container center" style={{ padding: '60px 0' }}>
          <h1 className="section-title">Chart not found</h1>
          <Link to="/charts" className="btn btn-primary">All dosing charts</Link>
        </div>
      </main>
    )
  }

  const cp = chart.preset
  const du = unitLabel(chart.doseUnit)
  const faq = buildFaq(p, chart)
  const related = CHART_SLUGS
    .filter((s) => s !== slug)
    .map((s) => getBySlug(s))
    .filter(Boolean)
    .slice(0, 6)
  const anyOverCap = chart.rows.some((r) => r.cells.some((c) => c.overCap))

  return (
    <main className="page">
      <Seo
        title={withBrand(`${p.name} Dosing Chart`)}
        description={`${p.name} dosing chart: how many insulin-syringe units to draw for common doses and bacteriostatic water amounts, from a ${cp.vialMg} mg vial — with a live calculator and a printable chart. Educational only.`}
        jsonLd={faq.length ? faqLd(`${p.name} dosing chart — FAQ`, faq) : undefined}
        image="/og/chart.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Dosing Charts', path: '/charts' },
          { name: `${p.name} Chart`, path: `/charts/${slug}` },
        ]}
      />
      <div className="container page-narrow">
        <Link to="/charts" className="pep-back">← All dosing charts</Link>

        <span className="pill blue" style={{ marginBottom: 10 }}>Dosing chart</span>
        <h1 className="section-title">{p.name} Dosing &amp; Reconstitution Chart</h1>
        <p className="section-sub" style={{ maxWidth: '64ch' }}>
          How many insulin-syringe units to draw for {p.name}, based on how much bacteriostatic
          water you add to a {cp.vialMg} mg vial. Every number below is on a U-100 (100&nbsp;units&nbsp;=&nbsp;1&nbsp;mL)
          syringe and matches our <Link to="/calculator">calculator</Link>.
        </p>
        <LastUpdated />

        <div className="chart-table-wrap">
          <table className="chart-table">
            <thead>
              <tr>
                <th scope="col">Water added</th>
                <th scope="col">Concentration</th>
                {chart.doses.map((d) => (
                  <th scope="col" key={d}>{d} {du} dose</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.rows.map((r) => (
                <tr key={r.waterMl}>
                  <th scope="row">{r.waterMl} mL</th>
                  <td className="soft">{r.concMgMl} mg/mL</td>
                  {r.cells.map((c, i) => (
                    <td key={i} className={c.overCap ? 'chart-over' : ''} title={c.overCap ? 'More than one full 1 mL syringe' : undefined}>
                      {fmtUnits(c.units)}{c.units != null ? ' u' : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small" style={{ marginTop: 8 }}>
          Cells show units to draw on a U-100 insulin syringe.
          {anyOverCap && ' Highlighted cells exceed one full 1 mL (100-unit) syringe — add more water, split the dose, or use a larger syringe.'}
        </p>

        <div className="guide-callout" style={{ margin: '18px 0' }}>
          🧮 <span>Different vial or dose? <Link to="/calculator"><strong>Adjust it in the calculator →</strong></Link></span>
        </div>

        <section className="pep-section">
          <h2 className="pep-h2">Calculate your exact {p.name} dose</h2>
          <p className="soft" style={{ marginTop: 0 }}>
            Pre-filled with {p.name}’s commonly cited vial and dose. Change any field to match your vial —
            it shows the exact units, concentration, and doses per vial.
          </p>
          <CalculatorForm initial={{ ...cp, name: p.name }} />
        </section>

        {faq.length > 0 && (
          <section className="pep-section">
            <h2 className="pep-h2">{p.name} dosing FAQ</h2>
            <div className="pep-faq">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="pep-section">
          <h2 className="pep-h2">More about {p.name}</h2>
          <div className="pep-related">
            <Link to={`/peptides/${slug}`} className="pep-related-card">
              <span className="pep-related-body">
                <span className="pep-related-name">{p.name} overview</span>
                <span className="pep-related-desc">Mechanism, researched uses, protocol, and cautions.</span>
              </span>
            </Link>
            <Link to="/peptide-dosage-guide" className="pep-related-card">
              <span className="pep-related-body">
                <span className="pep-related-name">Peptide dosage guide</span>
                <span className="pep-related-desc">The reconstitution formula, worked example, and common mistakes.</span>
              </span>
            </Link>
          </div>
        </section>

        {related.length > 0 && (
          <section className="pep-section">
            <h2 className="pep-h2">Other dosing charts</h2>
            <div className="chart-chips">
              {related.map((r) => (
                <Link key={r.slug} to={`/charts/${r.slug}`} className="chart-chip">{r.name}</Link>
              ))}
            </div>
          </section>
        )}

        <div className="pep-disclaimer">⚠️ {DISCLAIMER}</div>
      </div>
    </main>
  )
}
